import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DropdownMenu as DropdownMenuBase } from '../DropdownMenu'
import ListItem from '../ListItem'

dayjs.extend(calendar)

interface DocumentItemProps {
  archived?: boolean
  archivingEnabled?: boolean
  canDelete?: boolean
  canEdit?: boolean
  data: {
    archived?: boolean
    archiveMetadata?: {
      archivedAt?: Date | string
      archivedBy?: string
      reason?: string
    }
    carePlanName?: string
    createdAt?: Date | string
    id: string
    name: string
    originalFile?: string
    pdf?: string | { url?: string }
  }
  deleteLabel?: string
  onArchive?: (id: string) => void
  onDelete?: (id: string) => void
  onEdit?: (data: DocumentItemProps['data']) => void
  onRestore?: (id: string) => void
  readOnly?: boolean
  type?: string
}

const DropdownMenu = DropdownMenuBase as unknown as React.ComponentType<{
  buttonStyle?: React.CSSProperties
  buttonTestId?: string
  items: { label: string; onClick: () => void; testId?: string }[]
  menuStyle?: React.CSSProperties
}>

export default function DocumentItem({
  archived,
  archivingEnabled,
  canDelete = true,
  canEdit = true,
  data,
  deleteLabel,
  onArchive,
  onDelete,
  onEdit,
  onRestore,
  readOnly,
  type
}: DocumentItemProps) {
  const { t } = useTranslation(NAMESPACE.TEAM)

  const hiddenLinkRef = useRef<HTMLAnchorElement>(null)

  const isArchived = archived ?? data.archived ?? false

  const handleView = () => hiddenLinkRef.current?.click()
  const handleEdit = () => onEdit?.(data)
  const handleDelete = () => onDelete?.(data.id)
  const handleArchive = () => onArchive?.(data.id)
  const handleRestore = () => onRestore?.(data.id)

  const daysDiff = dayjs().diff(data.createdAt, 'days')
  const displayedTime =
    daysDiff <= 7
      ? dayjs(data.createdAt).calendar()
      : `Added ${daysDiff} days ago`

  const archivedOn = data.archiveMetadata?.archivedAt
  const displayedSubLabel =
    isArchived && archivedOn
      ? `${t(reportsConstants.DOCUMENT_ARCHIVED_ON)} ${dayjs(archivedOn).format(
          'DD-MM-YYYY'
        )}`
      : displayedTime

  const menuItems: {
    label: string
    onClick: () => void
    testId: string
  }[] = [
    {
      label: t(reportsConstants.DOCUMENT_ACTION_VIEW),
      onClick: handleView,
      testId: 'document-action-view'
    }
  ]

  if (isArchived) {
    if (canDelete && onRestore) {
      menuItems.push({
        label: t(reportsConstants.DOCUMENT_ACTION_RESTORE),
        onClick: handleRestore,
        testId: 'document-action-restore'
      })
    }
  } else {
    if (canEdit) {
      menuItems.push({
        label: t(reportsConstants.DOCUMENT_ACTION_EDIT),
        onClick: handleEdit,
        testId: 'document-action-edit'
      })
    }
    if (canDelete) {
      if (archivingEnabled && onArchive) {
        menuItems.push({
          label: t(reportsConstants.DOCUMENT_ACTION_ARCHIVE),
          onClick: handleArchive,
          testId: 'document-action-archive'
        })
      } else {
        menuItems.push({
          label: deleteLabel ?? t(reportsConstants.DOCUMENT_ACTION_DELETE),
          onClick: handleDelete,
          testId: 'document-action-delete'
        })
      }
    }
  }

  const label =
    data.name.length > 100 ? `${data.name.substring(0, 100)}...` : data.name

  return (
    <ListItem
      svgIcon="document"
      iconColor={colors.darkblue}
      inlineChildren
      label={label}
      multiSelect
      onSelect={
        type === 'edit' && !readOnly && !isArchived && canEdit
          ? handleEdit
          : handleView
      }
      subLabel={displayedSubLabel}
      carePlanName={data.carePlanName}
      iconSize={24}
      size={40}
      hasBorder
    >
      <ActionsContainer data-testid="document-item">
        {!readOnly && (
          <DropdownMenu
            items={menuItems}
            buttonStyle={dropdownStyles.menuIcon}
            buttonTestId="document-item-menu"
            menuStyle={dropdownStyles.menuContainer}
          />
        )}
        <HiddenLink
          href={
            data.originalFile ||
            (typeof data.pdf === 'string' ? data.pdf : data.pdf?.url)
          }
          ref={hiddenLinkRef}
          rel="noreferrer"
          target="_blank"
        >
          {' '}
        </HiddenLink>
      </ActionsContainer>
    </ListItem>
  )
}

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`

const HiddenLink = styled.a`
  left: -9999px;
  position: absolute;
`

const dropdownStyles = {
  menuContainer: {
    position: 'absolute' as const,
    top: 25,
    right: '-13px'
  },
  menuIcon: {
    alignItems: 'center',
    display: 'flex'
  }
}
